import { API_BASE_URL, authenticatedFetch } from '../config/api';
import type { Product } from './productService';

export type ProductToImageMode = 'ghost' | 'ai_model';
export type ProductToImageMannequinSource = 'system' | 'custom';
export type ProductToImageBackgroundColor =
  | 'pure_white'
  | 'soft_ivory'
  | 'warm_sand'
  | 'cool_gray'
  | 'deep_charcoal';
export type ProductToImageEnvironment =
  | 'studio'
  | 'cafe'
  | 'street'
  | 'showroom'
  | 'loft';
export type ProductToImagePose =
  | 'front_packshot'
  | 'three_quarter_packshot'
  | 'detail_packshot'
  | 'front_catalog'
  | 'three_quarter_catalog'
  | 'walk_catalog'
  | 'editorial_catalog';

export interface ProductToImageGeneratedImage {
  url: string;
  shotKey: string;
  shotLabel: string;
}

export interface ProductUploadValidationResult {
  valid: boolean;
  reason?: string;
  summary?: string;
  detectedCategory?: string;
  confidence?: number;
  hasSingleProduct?: boolean;
  isApparel?: boolean;
  quality?: 'low' | 'medium' | 'high';
}

export interface ProductToImageCreateInput {
  name: string;
  category: string;
  image: string;
  description?: string;
  mode: ProductToImageMode;
  backgroundColor: ProductToImageBackgroundColor;
  environment?: ProductToImageEnvironment;
  pose: ProductToImagePose;
  outputCount: number;
  mannequinSource?: ProductToImageMannequinSource;
  mannequinId?: string;
}

export interface ProductToImageHistoryItem {
  id: string;
  title: string;
  category: string;
  sku?: string;
  originalImageUrl?: string;
  generatedImageUrl?: string;
  generatedImageUrls?: string[];
  generatedImages?: ProductToImageGeneratedImage[];
  mode?: ProductToImageMode;
  backgroundColor?: ProductToImageBackgroundColor;
  environment?: ProductToImageEnvironment;
  pose?: ProductToImagePose;
  outputCount?: number;
  mannequinName?: string;
  mannequinSource?: ProductToImageMannequinSource;
  mannequinImageUrl?: string;
  generationStatus: 'completed' | 'failed' | 'pending';
  generationError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductToImageCreateResult {
  product: Product & {
    title?: string;
    metaData?: Record<string, any>;
  };
  generatedImageUrl?: string;
  generatedImageUrls?: string[];
  generatedImages?: ProductToImageGeneratedImage[];
  generationStatus: 'completed' | 'failed';
  generationError?: string;
  validation?: ProductUploadValidationResult;
}

export class ProductToImageService {
  static async validateUpload(
    image: string,
    category?: string
  ): Promise<ProductUploadValidationResult> {
    const response = await authenticatedFetch(`${API_BASE_URL}/products/validate-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image,
        category,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Gorsel validasyonu basarisiz oldu');
    }

    return data.data;
  }

  static async createFromImage(
    input: ProductToImageCreateInput
  ): Promise<ProductToImageCreateResult> {
    const response = await authenticatedFetch(`${API_BASE_URL}/products/create-from-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data?.data?.reason || data?.message || data?.error || 'Urun olusturma basarisiz oldu');
    }

    return data.data;
  }

  static async getHistory(limit: number = 50): Promise<ProductToImageHistoryItem[]> {
    const response = await authenticatedFetch(`${API_BASE_URL}/products/product-to-image/history?limit=${limit}`);
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data?.error?.message || data?.message || 'Gecmis uretimler yuklenemedi');
    }

    return data.data.items || [];
  }
}
